/**
 * MostLovedCard
 *
 * Compact list card for the blog index showing the top 3-5 posts by
 * recent positive reactions (loved + insightful + helpful, last 7d).
 * Renders nothing while loading/empty so the index doesn't reserve
 * blank space when the site is brand new and reactions are sparse.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Lightbulb, ThumbsUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiFetch } from '@/hooks/useFetch';

export const MostLovedCard = ({ isSpanish = false, days = 7, limit = 5 }) => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const lang = isSpanish ? '&lang=es' : '';
      const { data } = await apiFetch(
        `/api/blog/most-loved?days=${days}&limit=${limit}${lang}`
      );
      if (data?.posts) setPosts(data.posts);
      setLoaded(true);
    })();
  }, [isSpanish, days, limit]);

  // Don't render anything until we know there's data — keeps the
  // /blog page clean when the site is brand new.
  if (!loaded || posts.length === 0) return null;

  return (
    <Card
      className="border-[#00E5CC]/30 bg-gradient-to-br from-[#00E5CC]/5 to-transparent"
      data-testid="most-loved-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-[#00E5CC]" />
          {isSpanish
            ? `Más amados esta semana`
            : `Most loved this week`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-2.5" data-testid="most-loved-list">
          {posts.map((p, i) => (
            <li key={p.post_id || p.slug} className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00E5CC]/10 text-xs font-bold text-[#00A896]">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/blog/${p.slug || p.post_id}`}
                  className="line-clamp-2 text-sm font-medium leading-snug hover:text-[#00A896] transition-colors"
                  data-testid={`most-loved-item-${p.post_id || p.slug}`}
                >
                  {p.title}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {p.loved > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Heart className="h-3 w-3 text-rose-500" /> {p.loved}
                    </span>
                  )}
                  {p.insightful > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Lightbulb className="h-3 w-3 text-amber-500" /> {p.insightful}
                    </span>
                  )}
                  {p.helpful > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3 text-emerald-500" /> {p.helpful}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

export default MostLovedCard;
